/**
 * Foglio di riserva da stampare (A4): coppie/giocatori, partite per turno e campo
 * con le caselle per i game, fase finale e classifica da compilare a penna.
 * Serve se salta tutto: si scrive sul foglio e si inserisce nell'app dopo.
 */
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { errMsg, fmtWhen, gare, PHASE_LABEL, type GaraState, type Match } from "@/lib/gare";
import { garaPath, useBase } from "./ui";

const PRINT_CSS = `
@media print {
  @page { size: A4; margin: 11mm; }
  header, .ps-noprint { display: none !important; }
  body, #root, main { background: #fff !important; padding: 0 !important; margin: 0 !important; max-width: none !important; }
  .ps { font-size: 10.5px; color: #000; }
  .ps table { page-break-inside: auto; }
  .ps tr, .ps .ps-block { break-inside: avoid; }
}
.ps table { width: 100%; border-collapse: collapse; }
.ps th, .ps td { border: 1px solid #94a3b8; padding: 4px 6px; text-align: left; vertical-align: middle; }
.ps th { background: #f1f5f9; font-weight: 700; }
.ps .box { width: 34px; text-align: center; font-weight: 700; }
`;

function koPlaceholders(s: GaraState): { label: string; a: string; b: string }[] {
  const c = s.gara.config;
  const ko = s.matches.filter((m) => m.phase !== "group");
  if (s.gara.format !== "gironi_tabellone" || ko.length) return [];
  const groups = c.groups ?? 2;
  const per = c.per_group ?? 2;
  const out: { label: string; a: string; b: string }[] = [];
  const perGroup = (lab: string) => s.teams.filter((t) => (t.group_label || "A") === lab).length;
  if (groups === 2 && per === 2) {
    out.push({ label: "Semifinale Diamond 1", a: "1ª girone A", b: "2ª girone B" }, { label: "Semifinale Diamond 2", a: "1ª girone B", b: "2ª girone A" });
  } else if (groups === 1 && per === 4) {
    out.push({ label: "Semifinale Diamond 1", a: "1ª classificata", b: "4ª classificata" }, { label: "Semifinale Diamond 2", a: "2ª classificata", b: "3ª classificata" });
  } else {
    out.push({ label: "Finale Diamond", a: groups === 2 ? "1ª girone A" : "1ª classificata", b: groups === 2 ? "1ª girone B" : "2ª classificata" });
    return out;
  }
  out.push({ label: "Finale Diamond 1°-2°", a: "vincente Semifinale Diamond 1", b: "vincente Semifinale Diamond 2" });
  if (c.third_place !== false) out.push({ label: "Finale Diamond 3°-4°", a: "perdente Semifinale Diamond 1", b: "perdente Semifinale Diamond 2" });
  const gold = c.gold !== false && (groups === 2 ? perGroup("A") >= 4 && perGroup("B") >= 4 : s.teams.length >= 8);
  if (gold) {
    out.push(
      groups === 2
        ? { label: "Semifinale Gold 1", a: "3ª girone A", b: "4ª girone B" }
        : { label: "Semifinale Gold 1", a: "5ª classificata", b: "8ª classificata" },
      groups === 2
        ? { label: "Semifinale Gold 2", a: "3ª girone B", b: "4ª girone A" }
        : { label: "Semifinale Gold 2", a: "6ª classificata", b: "7ª classificata" },
      { label: "Finale Gold 5°-6°", a: "vincente Semifinale Gold 1", b: "vincente Semifinale Gold 2" },
      { label: "Finale Gold 7°-8°", a: "perdente Semifinale Gold 1", b: "perdente Semifinale Gold 2" },
    );
  }
  return out;
}

export default function PrintSheet() {
  const gid = Number(useParams().id);
  const base = useBase();
  const q = useQuery({ queryKey: ["gare", "gara", gid], queryFn: () => gare.get(gid) });
  if (q.isLoading) return <p className="p-6 text-sm text-slate-500">Caricamento…</p>;
  if (q.isError || !q.data) return <p className="p-6 text-sm text-red-600">Errore: {errMsg(q.error)}</p>;
  const s = q.data;
  const g = s.gara;
  const giro = g.format === "giro_a_8";
  const teams = Object.fromEntries(s.teams.map((t) => [t.id, t]));
  const label = (m: Match, side: "a" | "b"): string => {
    const l = side === "a" ? m.label_a : m.label_b;
    if (l) return l;
    const t = side === "a" ? m.team_a : m.team_b;
    return t ? teams[t]?.label ?? "" : "";
  };
  const matches = [...s.matches].sort((x, y) => (x.round_no ?? 0) - (y.round_no ?? 0) || String(x.court).localeCompare(String(y.court)));
  const showGroup = Object.keys(s.groups).length > 1;
  const players = s.players.filter((p) => p.role === "player").sort((a, b) => (a.seed ?? 99) - (b.seed ?? 99) || a.name.localeCompare(b.name));
  const instr = s.players.filter((p) => p.role === "instructor");
  const groupLabels = Array.from(new Set(s.teams.map((t) => t.group_label || "A"))).sort();

  return (
    <div className="ps mx-auto max-w-3xl bg-white p-4">
      <style>{PRINT_CSS}</style>
      <div className="ps-noprint mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <Link to={garaPath(base, gid)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ChevronLeft className="h-4 w-4" /> Torna al torneo
        </Link>
        <Button onClick={() => window.print()}><Printer className="h-4 w-4" /> Stampa</Button>
      </div>
      {!s.matches.length ? (
        <p className="ps-noprint mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
          Le partite non sono ancora generate: avvia il torneo e poi stampa il foglio.
        </p>
      ) : null}

      <div className="mb-3 flex items-center justify-between gap-3 border-b-2 border-slate-800 pb-2">
        <div>
          <h1 className="text-lg font-extrabold uppercase text-slate-900">{g.name}</h1>
          <p className="text-xs text-slate-600">{[fmtWhen(g.start_at), g.category, g.format_label].filter(Boolean).join(" · ")}</p>
        </div>
        <img src="https://www.movingpadel.it/img/moving_padel_new.png" alt="Moving Padel Monza" className="h-9 w-auto" />
      </div>
      <p className="mb-3 text-xs text-slate-600">
        Foglio di riserva: se manca la connessione scrivete qui i game e inseriteli nell'app appena possibile.
        {giro ? " Pareggio = ½ vittoria." : g.format === "gironi_tabellone" ? " Gironi a tempo (pareggio = ½). Fase Diamond (1°-4°) a set: 6 game, tie-break sul 6-6. Fase Gold (5°-8°): set ai 6 a tempo, niente pareggio." : " Pareggio = ½ vittoria."}
      </p>

      <div className="ps-block mb-4">
        <h2 className="mb-1 text-sm font-bold uppercase">{giro ? "Giocatori (numero dello schema)" : "Coppie"}</h2>
        {giro ? (
          <table>
            <tbody>
              {players.map((p, i) => <tr key={p.id}><td className="box">{i + 1}</td><td>{p.name}</td></tr>)}
              {instr.map((p, i) => <tr key={p.id}><td className="box">{instr.length === 1 ? "I" : `I${i + 1}`}</td><td>{p.name} (istruttore)</td></tr>)}
            </tbody>
          </table>
        ) : (
          <table>
            <thead><tr>{showGroup ? <th>Girone</th> : null}<th>N.</th><th>Coppia</th></tr></thead>
            <tbody>
              {groupLabels.flatMap((gl) => s.teams.filter((t) => (t.group_label || "A") === gl)
                .sort((a, b) => (a.seed ?? 0) - (b.seed ?? 0))
                .map((t) => (
                  <tr key={t.id}>{showGroup ? <td className="box">{gl}</td> : null}<td className="box">{t.seed}</td><td>{t.label}</td></tr>
                )))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mb-4">
        <h2 className="mb-1 text-sm font-bold uppercase">Partite</h2>
        <table>
          <thead>
            <tr><th>Turno</th><th>Campo</th>{showGroup ? <th>Fase</th> : null}<th>{giro ? "Coppia del turno" : "Coppia"}</th><th className="box">Game</th><th className="box">Game</th><th>{giro ? "Coppia del turno" : "Coppia"}</th></tr>
          </thead>
          <tbody>
            {matches.map((m) => (
              <tr key={m.id}>
                <td className="box">{m.round_no}</td>
                <td className="box">{m.court}</td>
                {showGroup ? <td>{m.phase === "group" ? `Girone ${m.group_label}` : PHASE_LABEL[m.phase]}</td> : null}
                <td>{label(m, "a")}</td>
                <td className="box">{m.status === "done" ? m.games_a : ""}</td>
                <td className="box">{m.status === "done" ? m.games_b : ""}</td>
                <td>{label(m, "b")}</td>
              </tr>
            ))}
            {koPlaceholders(s).map((k) => (
              <tr key={k.label}>
                <td colSpan={2}>{k.label}</td>
                {showGroup ? <td>Fase finale</td> : null}
                <td>{k.a}: ____________________</td>
                <td className="box" />
                <td className="box" />
                <td>{k.b}: ____________________</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ps-block">
        <h2 className="mb-1 text-sm font-bold uppercase">Classifica da compilare</h2>
        {(giro ? [{ key: "ind", title: "Classifica individuale", rows: players.map((p) => p.name) }]
          : groupLabels.map((gl) => ({
              key: gl, title: showGroup ? `Girone ${gl}` : "Classifica",
              rows: s.teams.filter((t) => (t.group_label || "A") === gl).sort((a, b) => (a.seed ?? 0) - (b.seed ?? 0)).map((t) => t.label),
            }))).map((tb) => (
          <table key={tb.key} className="mb-3">
            <thead><tr><th>{tb.title}</th><th className="box">V</th><th className="box">N</th><th className="box">P</th><th className="box">GF</th><th className="box">GS</th><th className="box">DG</th><th className="box">Pt</th><th className="box">Pos.</th></tr></thead>
            <tbody>{tb.rows.map((r) => <tr key={r}><td>{r}</td>{Array.from({ length: 8 }, (_, i) => <td key={i} className="box" />)}</tr>)}</tbody>
          </table>
        ))}
        <p className="text-[10px] text-slate-500">V = vinte · N = pareggiate (½ punto) · P = perse · GF/GS = game fatti/subiti · DG = differenza · Pt = punti. A parità: Pt → DG → GF{giro ? "" : " → scontro diretto"}.</p>
      </div>
    </div>
  );
}
